import * as React from 'react'

import {postSchoolLicense, deleteSchoolLicense} from '@frontrow/console/src/helpers/api-schools'
import SchoolLicensesListContainer from '@frontrow/console/src/components/common/school-licenses-list/school-licenses-list-container'
import {firstResponseApiError} from '@frontrow/console/src/entities/models/api-error'
import {useSchoolLicenses} from '@frontrow/console/src/hooks/use-school-licenses'

export type MultipleSubjectLicensesT = {
  startsAt: Date
  expiresAt: Date
  numMathLicenses: number
  numElaLicenses: number
  numSocialStudiesLicenses: number
  numScienceLicenses: number
}

export type SchoolLicensesListSchoolProps = {
  schoolId: number
  schoolName: string
  courseSubjectsByLicensing?: boolean
  setCourseSubjectsByLicensing?: (courseSubjectsByLicensing: boolean) => void
  enableModifications: boolean
}

const SchoolLicensesListHandler = ({
  schoolId,
  courseSubjectsByLicensing,
  ...rest
}: SchoolLicensesListSchoolProps): React.ReactElement => {
  const {data: licenses, error, isLoading, mutate} = useSchoolLicenses(schoolId)

  const addNewLicenses = async (licensesData: MultipleSubjectLicensesT): Promise<void> => {
    const {startsAt, expiresAt, ...licensesCount} = licensesData

    const subjectMapping = {
      numMathLicenses: 'math',
      numElaLicenses: 'ela',
      numSocialStudiesLicenses: 'social_studies',
      numScienceLicenses: 'science'
    } as const

    const promises = Object.keys(licensesCount).reduce(
      (acc: Array<Promise<unknown>>, subjectKey: string) => {
        const subjectCount = licensesCount[subjectKey as keyof typeof subjectMapping]

        if (!isNaN(subjectCount) && subjectCount > 0) {
          return [
            ...acc,
            postSchoolLicense(schoolId, {
              subject: subjectMapping[subjectKey as keyof typeof subjectMapping],
              startsAt,
              expiresAt,
              count: subjectCount
            })
          ]
        }
        return acc
      },
      []
    )

    try {
      await Promise.all(promises)
      await mutate()
    } catch (err: unknown) {
      const apiError = firstResponseApiError(err)
      throw new Error(apiError.message)
    }
  }

  const removeLicense = async (licenseId: number): Promise<void> => {
    try {
      await deleteSchoolLicense(licenseId)
      await mutate()
    } catch (err: unknown) {
      const apiError = firstResponseApiError(err)
      throw new Error(apiError.message)
    }
  }

  return (
    <SchoolLicensesListContainer
      addNewLicenses={addNewLicenses}
      removeLicense={removeLicense}
      licenses={licenses ?? []}
      isLoading={isLoading}
      error={error?.message ?? null}
      courseSubjectsByLicensing={courseSubjectsByLicensing === true}
      {...rest}
    />
  )
}

export default SchoolLicensesListHandler
